import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  ArrowRight,
  Files,
  FlaskConical,
  FolderOpen,
  FolderSync,
  LoaderCircle,
  Mail,
  Printer,
  QrCode,
  ScanLine,
  Unplug,
} from 'lucide-react';
import { hostHas } from '../shared/host-capabilities';

/** The scanner used last on this computer, remembered by name (indexes change when scanners come and go). */
const LAST_SCANNER_KEY = 'capturepoint.lastScanner';
export const readLastScanner = (): string | null => {
  try {
    return window.localStorage.getItem(LAST_SCANNER_KEY);
  } catch {
    return null;
  }
};
export const rememberLastScanner = (name: string) => {
  try {
    window.localStorage.setItem(LAST_SCANNER_KEY, name);
  } catch {
    /* storage refused: next scan asks again */
  }
};

/**
 * How a job starts (owner decision October 4, 2026). Each card does its job when it is
 * clicked: no select-then-Add-pages. Scan paper is ONE card however many scanners there
 * are: one scanner scans at once; several scan with the one used last (Change scanner
 * switches) or ask which the first time; with none it stays, unavailable, and says so. Until the
 * scanners have been looked for it says it is looking, and can't be used yet.
 * Break sheets matter only to paper, so their link sits under Scan paper.
 */
export function PageSourceCards({
  scannerNames,
  scannersListed = false,
  lastScanner,
  working,
  workingVerb,
  disabled,
  onPick,
  onPrintSeparatorSheets,
  scannerSources,
  onNoScanner,
}: {
  scannerNames: string[];
  /**
   * PAIGE: the host's sources with their own heading and art. A source that is not a scanner
   * (email to the job, a folder Paige Scan watches) is a card of its own, never "Scan paper".
   */
  scannerSources?: readonly {
    name: string;
    title?: string;
    icon?: 'scanner' | 'email' | 'folder';
  }[];
  /** The scanners have been looked for (an empty list before then is not "none"). */
  scannersListed?: boolean;
  lastScanner: string | null;
  /** The source being acquired right now, if any. */
  working: string | null;
  workingVerb: string;
  disabled: boolean;
  onPick: (key: string) => void;
  /** Absent where the host cannot print break sheets. */
  onPrintSeparatorSheets?: () => void;
  /** Scan paper is shown unavailable: no scanner was found (telemetry). */
  onNoScanner?: () => void;
}) {
  const id = useId();
  // PAIGE: scanners keep their place in the host's list (the key is that index).
  const sources = scannerSources ?? scannerNames.map((name) => ({ name }));
  const scannerIndexes = sources.flatMap((source, index) =>
    'icon' in source && source.icon && source.icon !== 'scanner' ? [] : [index],
  );
  const hostCards = sources.flatMap((source, index) =>
    'icon' in source && source.icon && source.icon !== 'scanner' ? [{ source, index }] : [],
  );
  const scanners = scannerIndexes.map((index) => sources[index].name);
  const noScanner = scannersListed && scanners.length === 0;
  useEffect(() => {
    if (noScanner) onNoScanner?.();
  }, [noScanner]);
  const [choosingScanner, setChoosingScanner] = useState(false);
  const scanArea = useRef<HTMLDivElement>(null);
  const remembered = lastScanner === null ? -1 : scanners.indexOf(lastScanner);
  const scanIndex = scanners.length === 1 ? 0 : remembered;
  const scanKey = scanIndex >= 0 ? `scanner:${scannerIndexes[scanIndex]}` : null;
  useEffect(() => {
    if (!choosingScanner) return;
    const outside = (event: Event) => {
      if (!(event.target instanceof Node) || !scanArea.current?.contains(event.target))
        setChoosingScanner(false);
    };
    window.document.addEventListener('pointerdown', outside, true);
    window.document.addEventListener('focusin', outside, true);
    return () => {
      window.document.removeEventListener('pointerdown', outside, true);
      window.document.removeEventListener('focusin', outside, true);
    };
  }, [choosingScanner]);
  const card = (
    key: string,
    title: string,
    detail: string,
    icon: ReactNode,
    onClick: () => void,
    extra: Record<string, unknown> = {},
  ) => {
    const busy = working !== null && working === key;
    // Named by its code for telemetry (data-telemetry), never by its words or the scanner it uses.
    return (
      <button
        key={key}
        type="button"
        className={`page-source-card${busy ? ' working' : ''}`}
        onClick={onClick}
        disabled={disabled}
        data-telemetry={
          key === 'files' ? 'source-files' : key === 'folder' ? 'source-folder' : 'source-scanner'
        }
        aria-label={title}
        aria-describedby={`${id}-${key}`}
        {...extra}
      >
        <span className="page-source-art" aria-hidden="true">
          {icon}
          <span />
        </span>
        <strong>{title}</strong>
        <small id={`${id}-${key}`}>{busy ? `${workingVerb}…` : detail}</small>
        <span className="page-source-go" aria-hidden="true">
          {busy ? <LoaderCircle className="spin" size={20} /> : <ArrowRight size={20} />}
        </span>
      </button>
    );
  };
  return (
    <div className="page-source-cards" role="group" aria-label="Page source">
      {card(
        'files',
        'Choose files',
        'PDFs or images from your computer',
        <Files size={42} strokeWidth={1.25} />,
        () => onPick('files'),
      )}
      {hostHas('folder-intake') &&
        card(
          'folder',
          'Bring in a folder',
          'Every file and subfolder',
          <FolderOpen size={42} strokeWidth={1.25} />,
          () => onPick('folder'),
        )}
      {scanners.length === 0 && !noScanner ? (
        // Still looking: not "No scanner detected" (the scanner may be there), and nothing to choose yet.
        <button
          type="button"
          className="page-source-card"
          disabled
          aria-label="Scan paper"
          aria-describedby={`${id}-looking`}
        >
          <span className="page-source-art" aria-hidden="true">
            <ScanLine size={42} strokeWidth={1.25} />
            <span />
          </span>
          <strong>Scan paper</strong>
          <small id={`${id}-looking`}>Looking for scanners…</small>
          <span className="page-source-go" aria-hidden="true">
            <LoaderCircle className="spin" size={20} />
          </span>
        </button>
      ) : noScanner ? (
        <button
          type="button"
          className="page-source-card unavailable"
          data-telemetry="source-scanner-missing"
          disabled
          aria-label="Scan paper"
          aria-describedby={`${id}-no-scanner`}
        >
          <span className="page-source-art" aria-hidden="true">
            <ScanLine size={42} strokeWidth={1.25} />
            <span />
          </span>
          <strong>Scan paper</strong>
          <small id={`${id}-no-scanner`} className="page-source-missing">
            <Unplug size={15} aria-hidden="true" /> No scanner detected
          </small>
        </button>
      ) : (
        <div
          className="page-source-scan"
          ref={scanArea}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && choosingScanner) {
              event.stopPropagation();
              setChoosingScanner(false);
            }
          }}
        >
          <div className="page-source-scan-card">
            {card(
              scanKey ?? 'scanner',
              'Scan paper',
              'Pages from your scanner',
              <ScanLine size={42} strokeWidth={1.25} />,
              () => (scanKey ? onPick(scanKey) : setChoosingScanner((open) => !open)),
              scanKey ? {} : { 'aria-haspopup': 'menu', 'aria-expanded': choosingScanner },
            )}
            {choosingScanner && (
              <div
                className="page-source-scanners"
                data-telemetry="source-scanner-choice"
                role="menu"
                aria-label="Which scanner?"
              >
                <span aria-hidden="true">Which scanner?</span>
                {scanners.map((name, index) => (
                  <button
                    key={`${index}-${name}`}
                    type="button"
                    role="menuitem"
                    autoFocus={index === Math.max(0, remembered)}
                    onClick={() => {
                      setChoosingScanner(false);
                      onPick(`scanner:${scannerIndexes[index]}`);
                    }}
                  >
                    <Printer size={17} aria-hidden="true" /> {name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="page-source-scan-links">
            {scanners.length > 1 && scanKey && (
              <button
                type="button"
                className="break-sheets-link"
                data-telemetry="source-change-scanner"
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={choosingScanner}
                onClick={() => setChoosingScanner((open) => !open)}
              >
                Change scanner
              </button>
            )}
            {onPrintSeparatorSheets && (
              <button type="button" className="break-sheets-link" onClick={onPrintSeparatorSheets}>
                <QrCode size={15} aria-hidden="true" /> Print break sheets
              </button>
            )}
          </div>
        </div>
      )}
      {hostCards.map(({ source, index }) =>
        card(
          `scanner:${index}`,
          source.title ?? source.name,
          source.name,
          'icon' in source && source.icon === 'email' ? (
            <Mail size={42} strokeWidth={1.25} />
          ) : (
            <FolderSync size={42} strokeWidth={1.25} />
          ),
          () => onPick(`scanner:${index}`),
        ),
      )}
    </div>
  );
}

/**
 * Simulate a scanner (owner decision October 4, 2026): a small way in for demonstrations
 * and testing. Its sample paperwork goes into THIS job and teaches it, so it asks first.
 */
export function SimulateScannerButton({
  jobName,
  disabled,
  onConfirm,
}: {
  jobName: string;
  disabled: boolean;
  onConfirm: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const area = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!asking) return;
    const outside = (event: Event) => {
      if (!(event.target instanceof Node) || !area.current?.contains(event.target))
        setAsking(false);
    };
    window.document.addEventListener('pointerdown', outside, true);
    return () => window.document.removeEventListener('pointerdown', outside, true);
  }, [asking]);
  const close = () => {
    setAsking(false);
    trigger.current?.focus();
  };
  return (
    <div
      className="simulate-scanner"
      ref={area}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && asking) {
          event.stopPropagation();
          close();
        }
      }}
    >
      <button
        ref={trigger}
        type="button"
        className="simulate-scanner-trigger"
        data-telemetry="source-simulator"
        disabled={disabled}
        aria-expanded={asking}
        aria-haspopup="dialog"
        onClick={() => setAsking((open) => !open)}
      >
        <FlaskConical size={16} aria-hidden="true" /> Simulate a scanner
      </button>
      {asking && (
        <div
          className="simulate-scanner-ask"
          role="dialog"
          aria-label="Add sample pages to this job?"
        >
          <strong>Add sample pages to this job?</strong>
          <span>{jobName} learns from them.</span>
          <div>
            <button type="button" className="secondary-button" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="primary-button"
              data-telemetry="source-simulator-confirm"
              autoFocus
              onClick={() => {
                setAsking(false);
                onConfirm();
              }}
            >
              Add sample pages
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
